import { ModelProvider } from "src/clients/agent-management";
import {
  ModelHosting,
  ModelLifecycle,
  ModelOption,
  ModelRegion,
} from "src/genai/ModelSelector/types";

/** A model the user should avoid for new work (superseded or deprecated). */
export const isOlderModel = (model: ModelOption): boolean =>
  model.lifecycle != null && model.lifecycle !== "current";

/**
 * Where inference runs, determined purely by who owns the server — orthogonal to
 * the routing region. Bedrock runs on AWS (surfaced as "Taktile-hosted"); every
 * other provider uses its own managed endpoint. A globally-routed Bedrock model
 * is still AWS-hosted; its residency is reported separately as "Global".
 */
export const hostingForModel = (card: {
  provider: ModelProvider;
}): ModelHosting => (card.provider === "bedrock" ? "aws" : "managed");

/**
 * Map the backend routing region to a residency facet. The value is a
 * human-readable label rather than a strict enum (e.g. "EU", "US/CA", "Global"),
 * so we match by substring.
 */
export const regionFromRouting = (
  routingTargetRegion: string | null | undefined,
): ModelRegion => {
  const value = (routingTargetRegion ?? "").toLowerCase();
  if (value.includes("eu")) return "eu";
  if (value.includes("au")) return "au";
  if (value.includes("us")) return "us";
  return "global";
};

/**
 * Lifecycle facet: a deprecated status wins, then a set `superseded_by` marks a
 * superseded model, otherwise it is current.
 */
export const lifecycleFor = (card: {
  status?: string | null;
  superseded_by?: string | null;
}): ModelLifecycle => {
  if (card.status === "deprecated") return "deprecated";
  if (card.superseded_by != null) return "superseded";
  return "current";
};

/**
 * The model we nudge toward: superseded models point via `superseded_by`,
 * deprecated ones via `deprecated_alternative`.
 */
export const successorIdFor = (card: {
  superseded_by?: string | null;
  deprecated_alternative?: string | null;
}): string | undefined =>
  card.superseded_by ?? card.deprecated_alternative ?? undefined;
